import type { ColumnsType } from 'antd/lib/table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { type TFunction, useTranslation } from 'react-i18next';
import { useGetTopSalesPersonsQuery } from '../../../../api/queries/getTopSalesPersons';
import { type StatisticFilterPayload, ModuleRole, ApplicationStage } from '../../../../api/types';
import { useCompany } from '../../../../components/contexts/CompanyContextManager';
import { useMultipleDealerIds } from '../../../../components/contexts/DealerContextManager';

export interface TopSalesManWithApplicationData {
    key: string;
    name: string;
    alias: string;
    noOfApplications: number;
}

export type TopSalesManData<RecordType = unknown> = {
    columns: ColumnsType<RecordType>;
    data: {
        currentMonth: RecordType[];
        lastMonth: RecordType[];
    };
};

const columns = (t: TFunction): ColumnsType<TopSalesManWithApplicationData> => [
    {
        ...t('dashboard:topSalesManWithApplication.tableColumns.name', { returnObjects: true }),
        dataIndex: 'name',
        key: 'name',
        fixed: true,
    },
    {
        ...t('dashboard:topSalesManWithApplication.tableColumns.alias', { returnObjects: true }),
        dataIndex: 'alias',
        key: 'alias',
    },
    {
        ...t('dashboard:topSalesManWithApplication.tableColumns.noOfApplications', { returnObjects: true }),
        dataIndex: 'noOfApplications',
        key: 'noOfApplications',
    },
];

const useTopSalesManWithApplicationData = () => {
    const { t } = useTranslation(['dashboard']);
    const cols = useMemo(() => columns(t), [t]);
    const [isLoading, setIsLoading] = useState(false);

    // prepare filter
    const company = useCompany(true);
    const { dealerIds } = useMultipleDealerIds();
    const filter: StatisticFilterPayload = useMemo(
        () => ({
            companyId: company?.id,
            dealerIds,
            moduleRole: ModuleRole.Application,
            stage: ApplicationStage.Financing,
        }),
        [company, dealerIds]
    );

    // fetch data
    const { data, loading, error } = useGetTopSalesPersonsQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            filter,
            sortParam: 'count',
        },
        skip: !company?.id,
    });

    useEffect(() => {
        setIsLoading(loading);
    }, [loading]);

    // transform response data
    const generateData = useCallback(
        async (applyData: (data: TopSalesManData<TopSalesManWithApplicationData>) => void) => {
            if (!data?.metrics) {
                applyData(null);

                return;
            }

            setIsLoading(true);

            const currentMonth: TopSalesManWithApplicationData[] =
                data.metrics.currentMonth?.map(({ name, alias, count }) => ({
                    key: name,
                    name,
                    alias,
                    noOfApplications: count,
                })) || [];
            const lastMonth: TopSalesManWithApplicationData[] =
                data.metrics.lastMonth?.map(({ name, alias, count }) => ({
                    key: name,
                    name,
                    alias,
                    noOfApplications: count,
                })) || [];

            applyData({ columns: cols, data: { currentMonth, lastMonth } });
            setIsLoading(false);
        },
        [data, cols]
    );

    return useMemo(
        () => ({
            isLoading,
            error,
            generateData,
        }),
        [isLoading, error, generateData]
    );
};

export default useTopSalesManWithApplicationData;
